import React from 'react';
import { View, Text, ActivityIndicator, FlatList, Pressable, StyleSheet } from 'react-native';
import { ChargerAttributes } from '../../models/charger-model';
import { getPartnerChargers, removePartnerCharger } from '../../services/partner-service';
import { isSuccessful } from '../../services/api-requests';
import { notificationService } from '../../services/notification-service';
import MyChargerPartnerInfoCard from './MyChargerPartnerInfoCard';
import { useChargerSparklineData } from '../../hooks/useChargerSparklineData';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { PartnerRevenueSplitTutorial } from '../Tutorials';
import { getTutorialStatus } from '../../services/tutorial-service';

type Props = {
    refreshKey?: number;
};

const PAGE_SIZE = 10;

const PartnerChargerList: React.FC<Props> = ({ refreshKey }) => {
    const navigation = useNavigation<any>();
    const user = useSelector((state: RootState) => state.user.userObj);
    const [loading, setLoading] = React.useState(false);
    const [all, setAll] = React.useState<ChargerAttributes[]>([]);
    const [page, setPage] = React.useState(1);
    const [meanderPartnerPage, setMeanderPartnerPage] = React.useState(1);
    const [revenueSplitPage, setRevenueSplitPage] = React.useState(1);
    const [observerPage, setObserverPage] = React.useState(1);
    const [showPartnerTutorial, setShowPartnerTutorial] = React.useState(false);
    
    // Collapsible state for each section
    const [meanderExpanded, setMeanderExpanded] = React.useState(true);
    const [splitExpanded, setSplitExpanded] = React.useState(true);
    const [observerExpanded, setObserverExpanded] = React.useState(true);

    // Categorize chargers based on permissions
    const meanderPartnerChargers = React.useMemo(() => {
        return all.filter(charger => charger.permissions?.includes('meander'));
    }, [all]);

    const revenueSplitChargers = React.useMemo(() => {
        return all.filter(charger => 
            charger.permissions?.includes('split') && !charger.permissions?.includes('meander')
        );
    }, [all]);

    const observerChargers = React.useMemo(() => {
        return all.filter(charger => {
            const permissions = charger.permissions || [];
            // Observer chargers have "read" or "observer" permission
            return (permissions.length === 1 && permissions[0] === 'read') || 
                   (permissions.includes('observer'));
        });
    }, [all]);

    const totalPages = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
    const meanderPartnerTotalPages = Math.max(1, Math.ceil(meanderPartnerChargers.length / PAGE_SIZE));
    const revenueSplitTotalPages = Math.max(1, Math.ceil(revenueSplitChargers.length / PAGE_SIZE));
    const observerTotalPages = Math.max(1, Math.ceil(observerChargers.length / PAGE_SIZE));

    const pagedData = React.useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return all.slice(start, start + PAGE_SIZE);
    }, [all, page]);

    const pagedMeanderPartnerData = React.useMemo(() => {
        const start = (meanderPartnerPage - 1) * PAGE_SIZE;
        return meanderPartnerChargers.slice(start, start + PAGE_SIZE);
    }, [meanderPartnerChargers, meanderPartnerPage]);

    const pagedRevenueSplitData = React.useMemo(() => {
        const start = (revenueSplitPage - 1) * PAGE_SIZE;
        return revenueSplitChargers.slice(start, start + PAGE_SIZE);
    }, [revenueSplitChargers, revenueSplitPage]);

    const pagedObserverData = React.useMemo(() => {
        const start = (observerPage - 1) * PAGE_SIZE;
        return observerChargers.slice(start, start + PAGE_SIZE);
    }, [observerChargers, observerPage]);

    // Fetch sparkline data for the currently displayed chargers
    const { sparklineData, loading: sparklineLoading } = useChargerSparklineData({
        chargers: pagedData,
        enabled: pagedData.length > 0,
    });

    const fetchChargers = React.useCallback(async () => {
        try {
            setLoading(true);
            const resp = await getPartnerChargers();
            // console.log('got partner chargers', resp.payload);
            if (isSuccessful(resp.status)) {
                const chargers = (resp.payload as any)?.chargers as ChargerAttributes[];
                setAll(Array.isArray(chargers) ? chargers : []);
                setPage(1);
            } else {
                notificationService.sendNotification('error', 'Unable to load partner chargers');
            }
        } catch (e: any) {
            notificationService.sendNotification('error', e?.message || 'Network error');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        void fetchChargers();
    }, [fetchChargers, refreshKey]);

    // Check if partner needs to see the revenue split tutorial
    React.useEffect(() => {
        const checkTutorialStatus = async () => {
            if (user?.uuid && (meanderPartnerChargers.length > 0 || revenueSplitChargers.length > 0)) {
                try {
                    const response = await getTutorialStatus();
                    if (response.status === 200 && response.payload) {
                        // If PARTNER_REVENUE_SPLIT tutorial hasn't been completed, show it
                        if (!response.payload.partnerSplitTutorial) {
                            setTimeout(() => {
                                setShowPartnerTutorial(true);
                            }, 1500); // Delay to let the page load first
                        }
                    }
                } catch (error) {
                    console.error('Error checking partner tutorial status:', error);
                    // Don't show tutorial if there's an error
                }
            }
        };
        
        checkTutorialStatus();
    }, [user?.uuid, meanderPartnerChargers.length, revenueSplitChargers.length]);

    const handleRemove = async (chargerId: string) => {
        try {
            const resp = await removePartnerCharger({ chargerId });
            if (isSuccessful(resp.status)) {
                notificationService.sendNotification('success', 'Removed');
                setAll(prev => prev.filter(c => c.uuid !== chargerId));
            } else {
                notificationService.sendNotification('error', (resp.payload as any)?.message || 'Failed to remove');
            }
        } catch (e: any) {
            notificationService.sendNotification('error', e?.message || 'Network error');
        }
    };

    const renderItem = ({ item, index }: { item: ChargerAttributes; index: number }) => {
        // Ensure we always have 7 days of data, never empty array
        const getSparklineData = () => {
            if (sparklineData && item.uuid && sparklineData[item.uuid]) {
                const data = sparklineData[item.uuid];
                // console.log('Using real sparkline data for', item.uuid, 'length:', data.length);
                return data;
            }
            
            // Fallback: create 7 days of zero data
            // console.log('Using fallback sparkline data for', item.uuid);
            const endDate = new Date();
            return Array.from({ length: 7 }, (_, index) => {
                const date = new Date(endDate);
                date.setDate(date.getDate() - (6 - index));
                return {
                    day: date.toISOString().split('T')[0],
                    totalKwh: 0,
                };
            });
        };

        const generateSparklineData = getSparklineData();
        // console.log('generateSparklineData', generateSparklineData);

        return (
            <MyChargerPartnerInfoCard
                index={index}
                data={item}
                onRemove={handleRemove}
                onShowHistory={(id) => {
                    if (!id) return;
                    try {
                        navigation.navigate('PartnerInsights', { preselectedChargerIds: [id] });
                    } catch (e: any) {
                        notificationService.sendNotification('error', e?.message || 'Unable to open Insights');
                    }
                }}
                sparklineData={generateSparklineData}
            />
        );
    };

    return (
        <View style={{ marginTop: 6 }}>
            {/* Partner Revenue Split Tutorial */}
            <PartnerRevenueSplitTutorial
                visible={showPartnerTutorial}
                onComplete={() => setShowPartnerTutorial(false)}
                onSkip={() => setShowPartnerTutorial(false)}
            />
            
            {loading ? (
                <View style={{ paddingVertical: 16 }}>
                    <ActivityIndicator color="#39BEB7" />
                </View>
            ) : all.length === 0 ? (
                <Text style={{ color: '#8D8E90' }}>No partner chargers yet.</Text>
            ) : (
                <>
                    {/* Meander Partner Chargers (First) */}
                    {meanderPartnerChargers.length > 0 && (
                        <View style={styles.section}>
                            <Pressable 
                                style={styles.sectionHeader}
                                onPress={() => setMeanderExpanded(!meanderExpanded)}
                            >
                                <View style={styles.sectionTitleContainer}>
                                    <Ionicons name="flash" size={20} color="#FFB800" />
                                    <Text style={styles.sectionTitle}>Meander Partner Chargers ({meanderPartnerChargers.length})</Text>
                                    <Ionicons 
                                        name={meanderExpanded ? "chevron-up" : "chevron-down"} 
                                        size={20} 
                                        color="#8D8E90" 
                                        style={{ marginLeft: 'auto' }}
                                    />
                                </View>
                            </Pressable>
                            {meanderExpanded && (
                                <>
                                    <FlatList
                                        data={pagedMeanderPartnerData}
                                        keyExtractor={(i, idx) => (i.uuid ?? idx).toString()}
                                        renderItem={renderItem}
                                        contentContainerStyle={{ paddingBottom: 12 }}
                                        scrollEnabled={false}
                                        nestedScrollEnabled={true}
                                    />
                                    {meanderPartnerTotalPages > 1 && (
                                        <View style={styles.pagination}>
                                            <Pressable
                                                onPress={() => setMeanderPartnerPage(p => Math.max(1, p - 1))}
                                                disabled={meanderPartnerPage <= 1}
                                                style={[styles.paginationButton, { backgroundColor: meanderPartnerPage <= 1 ? '#23262B' : '#39BEB7' }]}
                                            >
                                                <Text style={styles.paginationText}>Prev</Text>
                                            </Pressable>
                                            <Text style={styles.paginationInfo}>{`${meanderPartnerPage} / ${meanderPartnerTotalPages}`}</Text>
                                            <Pressable
                                                onPress={() => setMeanderPartnerPage(p => Math.min(meanderPartnerTotalPages, p + 1))}
                                                disabled={meanderPartnerPage >= meanderPartnerTotalPages}
                                                style={[styles.paginationButton, { backgroundColor: meanderPartnerPage >= meanderPartnerTotalPages ? '#23262B' : '#39BEB7' }]}
                                            >
                                                <Text style={styles.paginationText}>Next</Text>
                                            </Pressable>
                                        </View>
                                    )}
                                </>
                            )}
                        </View>
                    )}

                    {/* Revenue Split Chargers (Second) */}
                    {revenueSplitChargers.length > 0 && (
                        <View style={[styles.section, { marginTop: meanderPartnerChargers.length > 0 ? 20 : 0 }]}>
                            <Pressable 
                                style={styles.sectionHeader}
                                onPress={() => setSplitExpanded(!splitExpanded)}
                            >
                                <View style={styles.sectionTitleContainer}>
                                    <Ionicons name="cash" size={20} color="#51B868" />
                                    <Text style={styles.sectionTitle}>Revenue Split Chargers ({revenueSplitChargers.length})</Text>
                                    <Ionicons 
                                        name={splitExpanded ? "chevron-up" : "chevron-down"} 
                                        size={20} 
                                        color="#8D8E90" 
                                        style={{ marginLeft: 'auto' }}
                                    />
                                </View>
                            </Pressable>
                            {splitExpanded && (
                                <>
                                    <FlatList
                                        data={pagedRevenueSplitData}
                                        keyExtractor={(i, idx) => (i.uuid ?? idx).toString()}
                                        renderItem={renderItem}
                                        contentContainerStyle={{ paddingBottom: 12 }}
                                        scrollEnabled={false}
                                        nestedScrollEnabled={true}
                                    />
                                    {revenueSplitTotalPages > 1 && (
                                        <View style={styles.pagination}>
                                            <Pressable
                                                onPress={() => setRevenueSplitPage(p => Math.max(1, p - 1))}
                                                disabled={revenueSplitPage <= 1}
                                                style={[styles.paginationButton, { backgroundColor: revenueSplitPage <= 1 ? '#23262B' : '#39BEB7' }]}
                                            >
                                                <Text style={styles.paginationText}>Prev</Text>
                                            </Pressable>
                                            <Text style={styles.paginationInfo}>{`${revenueSplitPage} / ${revenueSplitTotalPages}`}</Text>
                                            <Pressable
                                                onPress={() => setRevenueSplitPage(p => Math.min(revenueSplitTotalPages, p + 1))}
                                                disabled={revenueSplitPage >= revenueSplitTotalPages}
                                                style={[styles.paginationButton, { backgroundColor: revenueSplitPage >= revenueSplitTotalPages ? '#23262B' : '#39BEB7' }]}
                                            >
                                                <Text style={styles.paginationText}>Next</Text>
                                            </Pressable>
                                        </View>
                                    )}
                                </>
                            )}
                        </View>
                    )}

                    {/* Observer Chargers (Last) */}
                    {observerChargers.length > 0 && (
                        <View style={[styles.section, { marginTop: (meanderPartnerChargers.length > 0 || revenueSplitChargers.length > 0) ? 20 : 0 }]}>
                            <Pressable 
                                style={styles.sectionHeader}
                                onPress={() => setObserverExpanded(!observerExpanded)}
                            >
                                <View style={styles.sectionTitleContainer}>
                                    <Ionicons name="eye" size={20} color="#3B82F6" />
                                    <Text style={styles.sectionTitle}>Observer Chargers ({observerChargers.length})</Text>
                                    <Ionicons 
                                        name={observerExpanded ? "chevron-up" : "chevron-down"} 
                                        size={20} 
                                        color="#8D8E90" 
                                        style={{ marginLeft: 'auto' }}
                                    />
                                </View>
                            </Pressable>
                            {observerExpanded && (
                                <>
                                    <FlatList
                                        data={pagedObserverData}
                                        keyExtractor={(i, idx) => (i.uuid ?? idx).toString()}
                                        renderItem={renderItem}
                                        contentContainerStyle={{ paddingBottom: 12 }}
                                        scrollEnabled={false}
                                        nestedScrollEnabled={true}
                                    />
                                    {observerTotalPages > 1 && (
                                        <View style={styles.pagination}>
                                            <Pressable
                                                onPress={() => setObserverPage(p => Math.max(1, p - 1))}
                                                disabled={observerPage <= 1}
                                                style={[styles.paginationButton, { backgroundColor: observerPage <= 1 ? '#23262B' : '#39BEB7' }]}
                                            >
                                                <Text style={styles.paginationText}>Prev</Text>
                                            </Pressable>
                                            <Text style={styles.paginationInfo}>{`${observerPage} / ${observerTotalPages}`}</Text>
                                            <Pressable
                                                onPress={() => setObserverPage(p => Math.min(observerTotalPages, p + 1))}
                                                disabled={observerPage >= observerTotalPages}
                                                style={[styles.paginationButton, { backgroundColor: observerPage >= observerTotalPages ? '#23262B' : '#39BEB7' }]}
                                            >
                                                <Text style={styles.paginationText}>Next</Text>
                                            </Pressable>
                                        </View>
                                    )}
                                </>
                            )}
                        </View>
                    )}
                </>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    section: {
        marginBottom: 12,
    },
    sectionHeader: {
        marginBottom: 12,
    },
    sectionTitleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    sectionTitle: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
    pagination: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 8,
    },
    paginationButton: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
    paginationText: {
        color: '#FFFFFF',
    },
    paginationInfo: {
        color: '#FFFFFF',
    },
});

export default PartnerChargerList;


